import { CreditCard, Pix } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Button, Checkbox, Chip, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { ModalSendEmail } from "../../../shared/components/ModalSendEmail/ModalSendEmail";
import { ISetup } from "../../../shared/services/api/financial/FinancialService";
import { globalBackgroundSetup, globalColorChipSetup, globalFormatCurrencyBRL, globalMaskDocument, globalTranslateStatusTaxSetup } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { ModalCancelSetup } from "../../financial/components/ModalCancelSetup";
import { ModalClientPaymentSetup } from "../../financial/components/ModalClientPaymentSetup";
import { ModalPaymentSetup } from "../../financial/components/ModalPaydSetup";
import { SetupMenu, SetupMenuModalType } from "./Menus/SetupMenu";
import { ModalDetailSetup } from "./Modals/ModalDetailSetup";

interface TableSetupFeesProps {
	rows: ISetup[];
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (newRowsPerPage: number) => void;
	dataIni: string;
	dataFim: string;
	onRefresh: () => void;
}

export const TableSetupFees = ({
	rows,
	page,
	rowsPerPage,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	dataIni,
	dataFim,
	onRefresh,
}: TableSetupFeesProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [batchAnchorEl, setBatchAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedRow, setSelectedRow] = useState<ISetup | null>(null);
	const [modalType, setModalType] = useState<SetupMenuModalType | null>(null);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const canView = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.View);
	const canUpdate = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.Update);
	const canAdd = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.Add);
	const canClientPayment = (row: ISetup) => row.status === "paid";

	const canManualPayment = (row: ISetup) => row.status === "pending";
	const canCancel = (row: ISetup) => row.status === "pending";
	const canSelectRow = (row: ISetup) =>
		(canUpdate && canManualPayment(row)) || (canAdd && canCancel(row)) || (canAdd && canClientPayment(row));
	const selectableRows = useMemo(() => rows.filter(canSelectRow), [rows]);
	const selectedRows = useMemo(() => rows.filter((row) => selectedIds.includes(row.id)), [rows, selectedIds]);
	const canBatchPayment = canUpdate && selectedRows.length > 0 && selectedRows.every(canManualPayment);
	const canBatchCancel = canAdd && selectedRows.length > 0 && selectedRows.every(canCancel);
	const canBatchClientPayment = canAdd && selectedRows.length > 0 && selectedRows.every(canClientPayment);
	const allSelected = selectableRows.length > 0 && selectedIds.length === selectableRows.length;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	useEffect(() => {
		setSelectedIds([]);
	}, [rows, page, rowsPerPage]);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: ISetup) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleBatchMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setBatchAnchorEl(event.currentTarget);
	};

	const handleBatchMenuClose = () => {
		setBatchAnchorEl(null);
	};

	const handleOpenModal = (type: SetupMenuModalType, row: ISetup) => {
		setSelectedRow(row);
		setModalType(type);
	};

	const handleCloseModal = () => {
		setSelectedRow(null);
		setModalType(null);
		handleBatchMenuClose();
	};

	const handleToggleRow = (rowId: string) => {
		setSelectedIds((currentIds) =>
			currentIds.includes(rowId) ? currentIds.filter((currentId) => currentId !== rowId) : [...currentIds, rowId]
		);
	};

	const handleToggleAll = (checked: boolean) => {
		setSelectedIds(checked ? selectableRows.map((row) => row.id) : []);
	};

	const handleOpenBatchModal = (type: SetupMenuModalType) => {
		setSelectedRow(null);
		setModalType(type);
		handleBatchMenuClose();
	};

	// Função ajustada para incluir o HtmlTooltip
	const getPaymentIcon = (method: string) => {
		let icon;
		let tooltipText;

		switch (method?.toLowerCase()) {
			case "pix":
				icon = <Pix sx={{ color: "#7bdcb5", fontSize: "large" }} />;
				tooltipText = "Pagamento: PIX";
				break;
			case "credit_card":
			case "boleto_credit_card":
				icon = <CreditCard fontSize="small" color="action" />;
				tooltipText = "Pagamento: Cartão de Crédito";
				break;
			default:
				return null;
		}
		return (
			<HtmlTooltip title={tooltipText} placement="top">
				<Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
					{icon}
				</Box>
			</HtmlTooltip>
		);
	};

	return (
		<TableContainer component={Paper} sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}>
			<Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={2} mb={2}>
				<Typography variant="body2" color="text.secondary">
					A seleção considera apenas as linhas exibidas nesta página.
				</Typography>
				{(canUpdate || canAdd) && (
					<Box>
						<Button variant="outlined" endIcon={<MoreVertIcon />} disabled={selectedRows.length === 0} onClick={handleBatchMenuOpen}>
							Ações selecionadas {selectedRows.length > 0 ? `(${selectedRows.length})` : ""}
						</Button>
						<Menu anchorEl={batchAnchorEl} open={Boolean(batchAnchorEl)} onClose={handleBatchMenuClose}>
							{canUpdate && (
								<MenuItem disabled={!canBatchPayment} onClick={() => handleOpenBatchModal("manualPayment")}>
									Baixar selecionadas
								</MenuItem>
							)}
							{canAdd && (
								<MenuItem disabled={!canBatchCancel} onClick={() => handleOpenBatchModal("cancel")}>
									Cancelar selecionadas
								</MenuItem>
							)}
							{canAdd && (
								<MenuItem disabled={!canBatchClientPayment} onClick={() => handleOpenBatchModal("clientPayment")}>
									Repasse imobiliária selecionadas
								</MenuItem>
							)}
						</Menu>
					</Box>
				)}
			</Box>
			<Table size="small">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						<TableCell padding="checkbox">
							<Checkbox checked={allSelected} indeterminate={partiallySelected} onChange={(event) => handleToggleAll(event.target.checked)} disabled={selectableRows.length === 0} />
						</TableCell>
						{["Data", "Imobiliária", "Cliente", "Vencimento", "Valor", "Valor recebido", "Valor repasse", "Status", "Tipo", "Ações"].map((label, i) => (
							<TableCell key={i} align={i === 8 ? "center" : i === 9 ? "right" : "left"} sx={i === 8 || i === 9 ? { px: 1 } : {}}>
								<Typography variant="button">{label}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => {
						const isSelected = selectedIds.includes(row.id);
						const canSelect = canSelectRow(row);

						return (
						<TableRow key={row.id} selected={isSelected} hover>
							<TableCell padding="checkbox">
								<Checkbox checked={isSelected} disabled={!canSelect} onChange={() => handleToggleRow(row.id)} />
							</TableCell>
							<TableCell>{formatDateFnsRemoveTimezone(row.created_at)}</TableCell>
							<TableCell>
								<Typography fontWeight="bold">{row.client_name}</Typography>
								<Typography variant="body2" color="textSecondary">
									{globalMaskDocument(row.client_document)}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography fontWeight="bold">{row.tenant_name}</Typography>
								<Typography variant="body2" color="textSecondary">
									{globalMaskDocument(row.tenant_document)}
								</Typography>
							</TableCell>
							<TableCell>{formatDateFnsRemoveTimezone(row.dueDate)}</TableCell>
							<TableCell>{globalFormatCurrencyBRL(row.raw_amount)}</TableCell>
							<TableCell>{row.credit_amount || row.credited_amount ? globalFormatCurrencyBRL(row.credit_amount ?? row.credited_amount ?? "0") : "-"}</TableCell>
							<TableCell>{row.client_amount ? globalFormatCurrencyBRL(row.client_amount) : "-"}</TableCell>
							<TableCell>
								<Chip
									size="medium"
									label={globalTranslateStatusTaxSetup(row.status)}
									sx={{
										color: globalColorChipSetup(row.status),
										fontWeight: 500,
										background: globalBackgroundSetup(row.status),
										border: "0.5px solid #f1f1f1",
									}}
								/>
							</TableCell>

							<TableCell
								align="center"
								sx={{ px: 0, width: "1%" }} // Reduz o padding e define largura mínima
							>
								{getPaymentIcon(row.payment_method) || "-"}
							</TableCell>

							<TableCell
								align="right"
								sx={{ p: 0, width: "1%" }} // Reduz o padding e define largura mínima
							>
								<IconButton onClick={(e) => handleMenuOpen(e, row)} size="small">
									<MoreVertIcon fontSize="small" />
								</IconButton>
							</TableCell>
						</TableRow>
						);
					})}
				</TableBody>
			</Table>

			{/* Paginação */}
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>

			{/* Menu de ações */}
			<SetupMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} selectedRow={selectedRow} onOpenModal={handleOpenModal} />

			{/* Modal de Detalhes */}
			{selectedRow && modalType === "details" && <ModalDetailSetup rowSetup={selectedRow} isOpenModal={true} onCloseModal={handleCloseModal} />}

			{/* Modal de Baixa Manual (Duplicado no código original, mantido por compatibilidade) */}
			{selectedRow && modalType === "manualPayment" && (
				<ModalPaymentSetup
					id={selectedRow.id}
					clientName={selectedRow.tenant_name}
					dueDate={selectedRow.dueDate}
					valueSetup={selectedRow.raw_amount}
					isOpenModal={true}
					isCloseModal={handleCloseModal}
					onclickConfirm={onRefresh}
				/>
			)}

			{!selectedRow && modalType === "manualPayment" && selectedRows.length > 0 && (
				<ModalPaymentSetup
					id=""
					clientName=""
					dueDate=""
					valueSetup="0"
					isOpenModal={true}
					isCloseModal={handleCloseModal}
					onclickConfirm={onRefresh}
					selectedSetups={selectedRows}
				/>
			)}

			{/* Modal de Cancelamento */}
			{selectedRow && modalType === "cancel" && (
				<ModalCancelSetup
					id={selectedRow.id}
					clientName={selectedRow.tenant_name}
					dueDate={selectedRow.dueDate}
					valueSetup={selectedRow.raw_amount}
					isOpenModal={true}
					isCloseModal={handleCloseModal}
					onclickConfirm={onRefresh}
				/>
			)}

			{!selectedRow && modalType === "cancel" && selectedRows.length > 0 && (
				<ModalCancelSetup
					id=""
					clientName=""
					dueDate=""
					valueSetup="0"
					isOpenModal={true}
					isCloseModal={handleCloseModal}
					onclickConfirm={onRefresh}
					selectedSetups={selectedRows}
				/>
			)}

			{selectedRow && modalType === "resendEmail" && (
				<ModalSendEmail
					id={selectedRow.id}
					email={selectedRow.tenant_email ?? ""}
					title="Reenvio de E-mail de Setup"
					modalSend={true}
					iscloseModal={handleCloseModal}
					onclickConfirm={onRefresh}
					onclickCancel={handleCloseModal}
				/>
			)}

			{selectedRow && modalType === "clientPayment" && (
				<ModalClientPaymentSetup rowSetup={selectedRow} isOpenModal={true} onCloseModal={handleCloseModal} onSuccess={onRefresh} />
			)}

			{!selectedRow && modalType === "clientPayment" && selectedRows.length > 0 && (
				<ModalClientPaymentSetup rowSetup={selectedRows[0]} selectedSetups={selectedRows} isOpenModal={true} onCloseModal={handleCloseModal} onSuccess={onRefresh} />
			)}
		</TableContainer>
	);
};
